import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";

/** Struck-off boxes of one kind; marks are filled in the given order, the first from the top left. */
export interface BoxMark {
  id: string;
  symbol: string;
  label: string;
  count: number;
}

/** A tapped box: its position on the whole track, the mark it shows and its position within that mark, both from 0. */
export interface BoxTap {
  index: number;
  mark?: BoxMark;
  markIndex: number;
}

/**
 * A track of boxes to strike off, e.g. points that can be lost in several ways. Rows split the boxes into levels;
 * each row may carry a label. Marks beyond the last box are not drawn; the system says what that means.
 * With onTap each box is a button; the arrow keys move between boxes, so the track is a single tab stop.
 */
export function BoxTrack(props: {
  label: string;
  rows: { boxes: number; label?: ReactNode; current?: boolean }[];
  marks: BoxMark[];
  onTap?: (tap: BoxTap) => void;
  /** Accessible name of a box, e.g. what tapping it does. */
  boxLabel?: (tap: BoxTap) => string;
}) {
  const symbols = props.marks.flatMap((m) => Array.from({ length: Math.max(0, m.count) }, (_, markIndex) => ({ mark: m, markIndex })));
  const total = props.rows.reduce((sum, row) => sum + Math.max(0, row.boxes), 0);
  const summary = props.marks
    .filter((m) => m.count > 0)
    .map((m) => `${m.count} ${m.label}`)
    .join(", ");
  const name = `${props.label}: ${summary || "nichts gestrichen"}`;
  const [focused, setFocused] = useState<number | null>(null);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const active = Math.min(focused ?? Math.min(symbols.length, total - 1), total - 1);

  const move = (event: KeyboardEvent, index: number) => {
    const next = { ArrowRight: index + 1, ArrowDown: index + 1, ArrowLeft: index - 1, ArrowUp: index - 1, Home: 0, End: total - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const target = Math.max(0, Math.min(total - 1, next));
    setFocused(target);
    buttons.current[target]?.focus();
  };

  let index = 0;
  return (
    <div className={`box-track${props.onTap ? " interactive" : ""}`} role={props.onTap ? "group" : "img"} aria-label={name}>
      {props.rows.map((row, r) => (
        <div key={r} className={`box-row${row.current ? " current" : ""}`}>
          {row.label != null && <span className="box-row-label">{row.label}</span>}
          <span className="boxes">
            {Array.from({ length: Math.max(0, row.boxes) }, (_, b) => {
              const i = index++;
              const shown = symbols[i];
              const className = `box${shown ? ` marked mark-${shown.mark.id}` : ""}`;
              if (!props.onTap) {
                return (
                  <span key={b} className={className} aria-hidden="true">
                    {shown?.mark.symbol}
                  </span>
                );
              }
              const tap: BoxTap = { index: i, mark: shown?.mark, markIndex: shown?.markIndex ?? 0 };
              return (
                <button
                  key={b}
                  ref={(element) => {
                    buttons.current[i] = element;
                  }}
                  type="button"
                  className={className}
                  tabIndex={i === active ? 0 : -1}
                  aria-label={props.boxLabel?.(tap) ?? `Kästchen ${i + 1}: ${shown?.mark.label ?? "frei"}`}
                  onFocus={() => setFocused(i)}
                  onKeyDown={(event) => move(event, i)}
                  onClick={() => props.onTap?.(tap)}
                >
                  <span aria-hidden="true">{shown?.mark.symbol}</span>
                </button>
              );
            })}
          </span>
        </div>
      ))}
    </div>
  );
}
